import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Chip from '@mui/material/Chip';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import VisibilityIcon from '@mui/icons-material/Visibility';

import { useAuth } from '../../providers/AuthProvider';
import { InspectionFormsApi } from '../../api-generated/apis';
import { apiConfig } from '../../api-generated/apiConfig';
import type { InspectionFormResponse } from '../../api-generated/models';
import { formatApiDate } from '../../utils/dateFormat';

const formsApi = new InspectionFormsApi(apiConfig);

const UserAssignmentsPage: React.FC = () => {
  const { email } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState(0);

  const formsQuery = useQuery<InspectionFormResponse[], Error>({
    queryKey: ['myForms', email],
    // getFormsByOperator returns a JSON array, but the generated typed deserializer collapses it
    // into a single (empty) object because the OpenAPI response is typed as one object. Read the
    // raw HTTP body and parse the array directly — same workaround as Schedules/Tasks pages.
    queryFn: async () => {
      if (!email) return [];
      const response = await formsApi.getFormsByOperatorRaw({ operatorId: email });
      const data = await response.raw.json();
      return (Array.isArray(data) ? data : data ? [data] : []) as InspectionFormResponse[];
    },
    enabled: !!email,
  });

  const activeTasks = (formsQuery.data ?? []).filter(
    (f: InspectionFormResponse) => f.status === 'PENDING' || f.status === 'IN_PROGRESS' || f.status === 'OVERDUE'
  );

  const pastTasks = (formsQuery.data ?? []).filter(
    (f: InspectionFormResponse) => f.status === 'COMPLETED' || f.status === 'CANCELLED'
  );

  const formatDate = (date?: unknown) => formatApiDate(date);

  const getStatusColor = (status?: string): 'default' | 'warning' | 'info' | 'success' | 'error' => {
    switch (status) {
      case 'PENDING': return 'default';
      case 'IN_PROGRESS': return 'info';
      case 'OVERDUE': return 'error';
      case 'COMPLETED': return 'success';
      case 'CANCELLED': return 'warning';
      default: return 'default';
    }
  };

  if (formsQuery.isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (formsQuery.isError) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Failed to load assignments: {formsQuery.error?.message}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" sx={{ mb: 3 }}>My Assignments</Typography>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 3 }}>
        <Tab label={`My Tasks (${activeTasks.length})`} />
        <Tab label={`Past Tasks (${pastTasks.length})`} />
      </Tabs>

      {tab === 0 && (
        <TaskTable
          tasks={activeTasks}
          formatDate={formatDate}
          getStatusColor={getStatusColor}
          onOpen={(id) => navigate(`/assignments/${id}`)}
          actionLabel="Continue"
          actionIcon={<PlayArrowIcon />}
          emptyMessage="No pending tasks. You're all caught up!"
        />
      )}

      {tab === 1 && (
        <TaskTable
          tasks={pastTasks}
          formatDate={formatDate}
          getStatusColor={getStatusColor}
          onOpen={(id) => navigate(`/assignments/${id}`)}
          actionLabel="View"
          actionIcon={<VisibilityIcon />}
          showCompletedAt
          emptyMessage="No completed tasks yet."
        />
      )}
    </Box>
  );
};

interface TaskTableProps {
  tasks: InspectionFormResponse[];
  formatDate: (date?: unknown) => string;
  getStatusColor: (status?: string) => 'default' | 'warning' | 'info' | 'success' | 'error';
  onOpen: (id: string) => void;
  actionLabel: string;
  actionIcon: React.ReactNode;
  showCompletedAt?: boolean;
  emptyMessage: string;
}

const TaskTable: React.FC<TaskTableProps> = ({
  tasks,
  formatDate,
  getStatusColor,
  onOpen,
  actionLabel,
  actionIcon,
  showCompletedAt,
  emptyMessage,
}) => {
  if (tasks.length === 0) {
    return (
      <Alert severity="info" sx={{ mt: 2 }}>{emptyMessage}</Alert>
    );
  }

  return (
    <TableContainer component={Paper} variant="outlined">
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Template</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Due Date</TableCell>
            {showCompletedAt && <TableCell>Completed</TableCell>}
            <TableCell>Assigned</TableCell>
            <TableCell align="right">Action</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {tasks.map((task) => (
            <TableRow key={task.id} hover>
              <TableCell>
                <Typography variant="body2" fontWeight={500}>
                  {task.templateId ?? 'Inspection'}
                </Typography>
                {task.machineId && (
                  <Typography variant="caption" color="text.secondary">
                    Machine: {task.machineId}
                  </Typography>
                )}
              </TableCell>
              <TableCell>
                <Chip label={task.status} size="small" color={getStatusColor(task.status)} />
              </TableCell>
              <TableCell>{formatDate(task.dueAt)}</TableCell>
              {showCompletedAt && <TableCell>{formatDate(task.completedAt)}</TableCell>}
              <TableCell>{formatDate(task.generatedAt)}</TableCell>
              <TableCell align="right">
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={actionIcon}
                  onClick={() => task.id && onOpen(task.id)}
                >
                  {actionLabel}
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default UserAssignmentsPage;
